/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Product, ProductVariant } from '@/types/api.types';
import { useAuthStore } from '@/store/authStore';
import { trackProductAddedToCart } from '@/lib/analytics/events';

export interface CartItem {
  product: Product;
  variantId?: string;
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  addItem: (product: Product, quantity?: number, variant?: ProductVariant) => void;
  removeItem: (productId: string, variantId?: string) => void;
  updateQuantity: (productId: string, quantity: number, variantId?: string) => void;
  clearCart: () => void;
  itemCount: number;
  cartTotal: number;
  isOpen: boolean;
  toggleCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const GUEST_KEY = 'cart_items_guest';
const keyFor = (userId?: string) => (userId ? `cart_items_${userId}` : GUEST_KEY);

const readCart = (key: string): CartItem[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as CartItem[]) : [];
  } catch (error) {
    console.warn(`Error reading cart "${key}":`, error);
    return [];
  }
};

const writeCart = (key: string, items: CartItem[]) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(items));
  } catch (error) {
    console.warn(`Error writing cart "${key}":`, error);
  }
};

/** Merge two carts, summing quantities for matching product + variant. */
const mergeCarts = (base: CartItem[], incoming: CartItem[]): CartItem[] => {
  const merged = [...base];
  incoming.forEach((item) => {
    const existing = merged.find(
      (m) => m.product.id === item.product.id && m.variantId === item.variantId
    );
    if (existing) {
      existing.quantity += item.quantity;
    } else {
      merged.push({ ...item });
    }
  });
  return merged;
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthStore();
  const userId = user?.id;
  const cartKey = keyFor(userId);

  const [items, setItems] = useState<CartItem[]>(() => readCart(cartKey));
  const [isOpen, setIsOpen] = useState(false);
  const prevKeyRef = useRef(cartKey);

  // Handle auth transitions. When a guest signs in at checkout, their guest
  // cart is merged into the (possibly existing) user cart so nothing is lost.
  useEffect(() => {
    if (prevKeyRef.current === cartKey) return;
    const prevKey = prevKeyRef.current;
    prevKeyRef.current = cartKey;

    if (userId && prevKey === GUEST_KEY) {
      const guestItems = readCart(GUEST_KEY);
      const userItems = readCart(cartKey);
      const merged = mergeCarts(userItems, guestItems);
      writeCart(cartKey, merged);
      window.localStorage.removeItem(GUEST_KEY);
      setItems(merged);
    } else {
      // Logout, or switching between accounts: load the target cart.
      setItems(readCart(cartKey));
    }
  }, [cartKey, userId]);

  // Persist the active cart whenever it changes.
  useEffect(() => {
    writeCart(cartKey, items);
  }, [items, cartKey]);

  // Calculate derived state
  const itemCount = items.reduce((total, item) => total + item.quantity, 0);
  const cartTotal = items.reduce((total, item) => {
    const variant = item.variantId ? item.product.variants?.find(v => v.id === item.variantId) : null;
    const price = variant ? parseFloat(variant.price) : parseFloat(item.product.price);
    return total + price * item.quantity;
  }, 0);

  const addItem = (product: Product, quantity = 1, variant?: ProductVariant) => {
    setItems((currentItems) => {
      const existingItem = currentItems.find(
        (item) => item.product.id === product.id && item.variantId === variant?.id
      );

      if (existingItem) {
        return currentItems.map((item) =>
          item.product.id === product.id && item.variantId === variant?.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [...currentItems, { product, quantity, variantId: variant?.id }];
    });
    trackProductAddedToCart(product, quantity, variant);
    setIsOpen(true);
  };

  const removeItem = (productId: string, variantId?: string) => {
    setItems((currentItems) =>
      currentItems.filter((item) => !(item.product.id === productId && item.variantId === variantId))
    );
  };

  const updateQuantity = (productId: string, quantity: number, variantId?: string) => {
    if (quantity < 1) {
      removeItem(productId, variantId);
      return;
    }

    setItems((currentItems) =>
      currentItems.map((item) =>
        (item.product.id === productId && item.variantId === variantId)
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const toggleCart = () => setIsOpen((prev) => !prev);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        itemCount,
        cartTotal,
        isOpen,
        toggleCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
